import { useDisclosure } from '@mantine/hooks';
import PickGlobalPermissionsModal from 'components/modals/pick-global-permissions-modal/PickGlobalPermissionsModal';
import { createContext, useContext, useState } from 'react';
import { OrganizationModuleFlags } from 'features/organization-modules/use-organization-modules';

/**
 * Parameters for opening the global permissions picker.
 */
interface PickGlobalPermissionsParams {
  title: string;
  initialPermissions: number[];
  onPick: (permissions: number[]) => void;
  moduleFlags?: OrganizationModuleFlags;
}

/**
 * Function for selecting direct global permissions.
 */
type PickGlobalPermissions = (params: PickGlobalPermissionsParams) => void;

/**
 * Context interface for the global permissions picker.
 */
interface IPickGlobalPermissionsContext {
  pickGlobalPermissions: PickGlobalPermissions;
}

const PickGlobalPermissionsContext = createContext<IPickGlobalPermissionsContext>(undefined!);

/**
 * Provider for selecting direct global permissions.
 */
export function PickGlobalPermissionsProvider({ children }: { children: React.ReactNode }) {
  const [opened, { close, open }] = useDisclosure(false);
  const [params, setParams] = useState<PickGlobalPermissionsParams | undefined>(undefined);

  /**
   * Opens the modal for editing direct global permissions.
   */
  const pickGlobalPermissions: PickGlobalPermissions = (nextParams) => {
    setParams(nextParams);
    open();
  };

  return (
    <PickGlobalPermissionsContext.Provider value={{ pickGlobalPermissions }}>
      {children}
      {opened && <PickGlobalPermissionsModal opened onClose={close} {...params!} />}
    </PickGlobalPermissionsContext.Provider>
  );
}

/**
 * Hook for selecting direct global permissions.
 */
export function usePickGlobalPermissions() {
  return useContext(PickGlobalPermissionsContext);
}
